const measurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID || 'G-78KP68EXMW';
const enabled = process.env.REACT_APP_GA4_TRACKING_ENABLED === 'true' && Boolean(measurementId);
let userEligible = true;
let activePurchase = null;

export const isGa4TrackingEnabled = () => enabled;

export function setGa4User(user) {
  userEligible = user?.analytics?.enabled !== false;
  if (enabled && typeof window.gtag === 'function') {
    try {
      window.gtag('set', { user_id: userEligible && user?.id ? String(user.id) : null });
    } catch (error) {
      console.warn('Unable to set GA4 user', error);
    }
  }
}

export function sendGa4Event(name, params = {}) {
  if (enabled && userEligible && typeof window.gtag === 'function') {
    try {
      window.gtag('event', name, { send_to: measurementId, ...params });
      return true;
    } catch (error) {
      console.warn('Unable to send GA4 event', error);
    }
  }
  return false;
}

export function sendGa4Signup(signup) {
  if (!signup?.event_id || !signup.user_id || !signup.method) return;
  userEligible = true;
  try {
    const key = `ga4_${signup.event_id}`;
    if (window.localStorage.getItem(key)) return;
    if (sendGa4Event('sign_up', { method: signup.method, user_id: String(signup.user_id) })) window.localStorage.setItem(key, '1');
  } catch (error) {
    console.warn('Unable to record GA4 signup', error);
  }
}

export async function getGa4Context() {
  if (!enabled || !userEligible || typeof window.gtag !== 'function') return {};
  const getValue = (field) => new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(null), 1000);
    try {
      window.gtag('get', measurementId, field, (value) => {
        clearTimeout(timeout);
        resolve(value == null ? null : String(value));
      });
    } catch (error) {
      clearTimeout(timeout);
      resolve(null);
    }
  });
  const [clientId, sessionId] = await Promise.all([getValue('client_id'), getValue('session_id')]);
  return { ...(clientId ? { ga_client_id: clientId } : {}), ...(sessionId ? { ga_session_id: sessionId } : {}) };
}

export function trackGa4Checkout(analytics) {
  if (!analytics?.checkout) return;
  try {
    if (analytics.purchase_ref) window.sessionStorage.setItem('ga4_purchase_ref', analytics.purchase_ref);
    else window.sessionStorage.removeItem('ga4_purchase_ref');
    sendGa4Event('begin_checkout', analytics.checkout);
  } catch (error) {
    console.warn('Unable to record GA4 checkout', error);
  }
}

export function trackGa4Purchase(client) {
  if (!enabled || !userEligible) return Promise.resolve();
  let reference;
  try {
    reference = new URL(window.location.href).searchParams.get('ga4_purchase_ref') || window.sessionStorage.getItem('ga4_purchase_ref');
  } catch (error) { return Promise.resolve(); }
  if (!reference) return Promise.resolve();
  if (activePurchase?.reference === reference) return activePurchase.promise;

  const promise = (async () => {
    for (let attempt = 0; attempt < 5; attempt += 1) {
      try {
        const response = await client.get(`analytics/ga4/purchases/${encodeURIComponent(reference)}`);
        if (response.data?.status === 'ready' && response.data?.purchase) {
          const key = `ga4_purchase_${reference}`;
          if (!window.sessionStorage.getItem(key)) {
            if (!sendGa4Event('purchase', response.data.purchase)) return;
            window.sessionStorage.setItem(key, '1');
          }
          if (window.sessionStorage.getItem('ga4_purchase_ref') === reference) window.sessionStorage.removeItem('ga4_purchase_ref');
          return;
        }
      } catch (error) {
        if (attempt === 4) console.warn('Unable to retrieve GA4 purchase', error);
      }
      if (attempt < 4) await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  })().finally(() => { activePurchase = null; });
  activePurchase = { reference, promise };
  return promise;
}
